import { useEffect, useState } from "react";
import { ArrowClockwise, ShareNetwork } from "@phosphor-icons/react";
import { api } from "../../lib/api";

export default function Referrals() {
  const [referrers, setReferrers] = useState(null);
  const [error, setError] = useState("");

  const load = () => {
    api.get("/referrals/summary").then((r) => setReferrers(r.data)).catch(() => setError("Could not load referrals. Please try again."));
  };

  useEffect(() => {
    load();
  }, []);

  const origin = window.location.origin;

  return (
    <div data-testid="admin-referrals">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl text-primary tracking-tight">Referral program</h1>
          <p className="mt-2 text-sm text-foreground/60" data-testid="admin-referrals-count">
            {referrers ? `${referrers.length} referrer${referrers.length === 1 ? "" : "s"}` : "Loading…"}
          </p>
        </div>
        <button
          data-testid="admin-referrals-refresh"
          onClick={load}
          className="flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-primary transition-colors duration-300"
        >
          <ArrowClockwise size={15} weight="bold" /> Refresh
        </button>
      </div>
      {error && <p className="mt-4 text-sm font-semibold text-destructive" data-testid="admin-referrals-error">{error}</p>}

      <div className="mt-10 bg-card border border-border rounded-sm overflow-x-auto" data-testid="admin-referrals-table-wrap">
        <table className="w-full text-sm min-w-[860px]" data-testid="admin-referrals-table">
          <thead>
            <tr className="border-b border-border text-left">
              {["Referrer", "Email", "Personal Link", "Referrals", "Reward Owed"].map((h) => (
                <th key={h} className="px-5 py-4 text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {referrers && referrers.length === 0 && (
              <tr>
                <td colSpan={5} className="px-5 py-14 text-center text-foreground/50 font-medium" data-testid="admin-referrals-empty">
                  No referrers yet — they'll appear here when someone creates a personal link.
                </td>
              </tr>
            )}
            {referrers &&
              referrers.map((r) => (
                <tr key={r.code} className="border-b border-border last:border-0 hover:bg-secondary/40 transition-colors duration-200" data-testid={`admin-referrer-row-${r.code}`}>
                  <td className="px-5 py-4 font-semibold text-foreground whitespace-nowrap">{r.name}</td>
                  <td className="px-5 py-4 text-foreground/70">{r.email}</td>
                  <td className="px-5 py-4">
                    <code className="text-xs font-semibold text-accent bg-accent/10 rounded-full px-3 py-1.5" data-testid={`admin-referrer-link-${r.code}`}>
                      {origin}/?ref={r.code}
                    </code>
                  </td>
                  <td className="px-5 py-4 font-bold text-foreground" data-testid={`admin-referrer-count-${r.code}`}>{r.referrals}</td>
                  <td className="px-5 py-4">
                    {r.referrals > 0 ? (
                      <span className="text-xs font-bold uppercase tracking-wider text-primary bg-primary/10 border border-primary/30 rounded-full px-3 py-1.5" data-testid={`admin-referrer-reward-${r.code}`}>
                        {r.referrals} free month{r.referrals === 1 ? "" : "s"}
                      </span>
                    ) : (
                      <span className="text-xs font-semibold text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 flex items-start gap-3 text-xs text-foreground/50 max-w-2xl" data-testid="admin-referrals-note">
        <ShareNetwork size={16} weight="duotone" className="text-accent shrink-0 mt-0.5" />
        <p className="leading-relaxed">
          A referral is credited when someone arriving via a personal link submits the consultation form.
          One reward per referred person, ever. Rewards are owed as free Compass Membership months — honor them at your discretion and mark the lead accordingly.
        </p>
      </div>
    </div>
  );
}
